import { useState, useEffect, useContext, useMemo } from "react";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";

// My Files
import { DatabaseContext } from "../../../context";
import { shouldFileBeVisibleToUser, downloadBlob } from "../../../utility";

export default function FileUsageDialog(props) {
    const { getFileUsage, users, groups, comments } =
        useContext(DatabaseContext);

    // const { currentUserData } = useContext(UserContext);

    const [rows, setRows] = useState([]);
    const [isVideo, setIsVideo] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedFileUsage, setLoadedFileUsage] = useState(null);
    const [loadedFileKey, setLoadedFileKey] = useState(null);
    const [userSearch, setUserSearch] = useState("");

    const fileKey = props.file ? props.file.key : null;

    useEffect(() => {
        let isActive = true;

        if (!props.open || !fileKey) {
            setIsLoading(false);
            setLoadedFileUsage(null);
            setLoadedFileKey(null);
            setRows([]);
            return () => {
                isActive = false;
            };
        }

        setIsLoading(true);
        setLoadedFileUsage(null);
        setLoadedFileKey(null);
        setRows([]);

        getFileUsage(true).then((usageData) => {
            if (!isActive) return;

            setLoadedFileUsage(usageData);
            setLoadedFileKey(fileKey);
        });

        return () => {
            isActive = false;
        };
    }, [props.open, fileKey, getFileUsage]);

    useEffect(() => {
        if (props.file && loadedFileUsage !== null) {
            // Find all the users that can access the file, and compile the usage data for each
            let rowDataArray = [];
            console.log("Compiling Usage Data:");

            var totalViewCount = 0;
            var totalDownloadCount = 0;
            var totalCommentCount = 0;
            var totalTimelineCount = 0;
            var totalPlaylistCount = 0;
            var totalPlaylistShareCount = 0;

            users.forEach((user) => {
                if (
                    props.file.managers.includes(user.key) ||
                    shouldFileBeVisibleToUser(props.file, groups, user)
                ) {
                    // Now compile the data for each row, based on the file usage objects, comments, timelines
                    // and playlists

                    // Row data from file usage objects

                    var viewCount = 0;
                    var downloadCount = 0;
                    var timelineCount = 0;
                    var playlistCount = 0;
                    var playlistShareCount = 0;

                    let matchingFileUsages = loadedFileUsage.filter(
                        (usage) =>
                            props.file.key === usage.file &&
                            user.key === usage.user
                    );

                    matchingFileUsages.forEach((usage) => {
                        if (usage.action === "Open") {
                            viewCount += 1;
                        } else if (usage.action === "Download") {
                            downloadCount += 1;
                        } else if (usage.action === "TimelineCreate") {
                            timelineCount += 1;
                        } else if (usage.action === "PlaylistCreate") {
                            playlistCount += 1;
                        } else if (usage.action === "PlaylistShare") {
                            var userKeysSharedTo = [];

                            usage.usersSharedWith.forEach((sharedUserKey) => {
                                userKeysSharedTo.push(sharedUserKey);
                            });

                            usage.groupsSharedWith.forEach((group) => {
                                let groupObject = groups.find(
                                    (g) => g.key === group.key
                                );

                                if (groupObject) {
                                    groupObject.users.forEach((groupUser) => {
                                        userKeysSharedTo.push(groupUser.key);
                                    });
                                }
                            });

                            playlistShareCount += userKeysSharedTo.length;
                        }
                    });

                    // Row data from comments
                    var commentCount = 0;

                    let matchingComments = comments.filter(
                        (comment) =>
                            props.file.key === comment.file &&
                            user.key === comment.user
                    );

                    commentCount = matchingComments.length;

                    // Create the row object
                    rowDataArray.push({
                        name: user.fullName,
                        viewCount: viewCount,
                        commentCount: commentCount,
                        downloadCount: downloadCount,
                        timelineCount: timelineCount,
                        playlistCount: playlistCount,
                        playlistShareCount: playlistShareCount,
                    });

                    // Update the totals
                    totalViewCount += viewCount;
                    totalCommentCount += commentCount;
                    totalDownloadCount += downloadCount;
                    totalTimelineCount += timelineCount;
                    totalPlaylistCount += playlistCount;
                    totalPlaylistShareCount += playlistShareCount;
                }
            });

            // Most file views at top of table

            rowDataArray = rowDataArray.sort((a, b) =>
                a.viewCount < b.viewCount ? 1 : -1
            );

            // Create total row
            rowDataArray.push({
                name: "Total",
                isTotal: true,
                viewCount: totalViewCount,
                commentCount: totalCommentCount,
                downloadCount: totalDownloadCount,
                timelineCount: totalTimelineCount,
                playlistCount: totalPlaylistCount,
                playlistShareCount: totalPlaylistShareCount,
            });

            setRows(rowDataArray);

            setIsVideo(Boolean(props.isVideo));

            setIsLoading(false);
        }
        // eslint-disable-next-line
    }, [props, loadedFileUsage]);

    const handleClose = () => {
        props.onClose();
    };

    const filteredRows = useMemo(() => {
        const searchTerm = userSearch.trim().toLocaleLowerCase();

        if (!searchTerm) return rows;

        const matchingRows = rows.filter(
            (row) =>
                !row.isTotal &&
                row.name.toLocaleLowerCase().includes(searchTerm)
        );

        if (matchingRows.length === 0) return [];

        const totalRow = matchingRows.reduce(
            (total, row) => ({
                ...total,
                viewCount: total.viewCount + row.viewCount,
                commentCount: total.commentCount + row.commentCount,
                downloadCount: total.downloadCount + row.downloadCount,
                timelineCount: total.timelineCount + row.timelineCount,
                playlistCount: total.playlistCount + row.playlistCount,
                playlistShareCount:
                    total.playlistShareCount + row.playlistShareCount,
            }),
            {
                name: "Total",
                isTotal: true,
                viewCount: 0,
                commentCount: 0,
                downloadCount: 0,
                timelineCount: 0,
                playlistCount: 0,
                playlistShareCount: 0,
            }
        );

        return [...matchingRows, totalRow];
    }, [rows, userSearch]);

    function handleDownloadPress() {
        var csvString =
            "Shared With, Page View Count, Comment Count, Download Count, Timeline Count, Playlist Count, Playlist Share Count\n";

        filteredRows.forEach((row) => {
            csvString += row.name.replace(",", "") + ", ";
            csvString += row.viewCount + ", ";
            csvString += row.commentCount + ", ";
            csvString += row.downloadCount + ", ";
            csvString += row.timelineCount + ", ";
            csvString += row.playlistCount + ", ";
            csvString += row.playlistShareCount + "\n";
        });

        downloadBlob(csvString, "export.csv", "text/csv;charset=utf-8;");
    }

    const showLoading =
        isLoading || loadedFileUsage === null || loadedFileKey !== fileKey;

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="file-usage-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="lg"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: {
                            xs: "calc(100% - 16px)",
                            sm: "calc(100% - 48px)",
                        },
                        m: { xs: 1, sm: 3 },
                        width: {
                            xs: "calc(100% - 16px)",
                            sm: "calc(100% - 48px)",
                        },
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <DataUsageOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="file-usage-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        File usage
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {props.file?.displayName}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close file usage"
                    onClick={handleClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {showLoading ? (
                    <Box
                        sx={{
                            minHeight: { xs: 300, sm: 420 },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1.5,
                            textAlign: "center",
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <CircularProgress size={34} thickness={4} />
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800 }}
                            >
                                Loading usage data
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.25 }}
                            >
                                Compiling activity for this file…
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "stretch", sm: "center" },
                                justifyContent: "space-between",
                                gap: 1.5,
                                px: { xs: 1.5, sm: 2 },
                                py: 1.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Usage by user
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Views and interactions from users with
                                    access to this file.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "stretch", sm: "center" },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    label="Search users"
                                    placeholder="Type a user name"
                                    size="small"
                                    value={userSearch}
                                    onChange={(event) =>
                                        setUserSearch(event.target.value)
                                    }
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: userSearch ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        aria-label="Clear user search"
                                                        onClick={() =>
                                                            setUserSearch("")
                                                        }
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={{ minWidth: { sm: 260 } }}
                                />
                                <Button
                                    onClick={handleDownloadPress}
                                    variant="outlined"
                                    startIcon={<DownloadOutlinedIcon />}
                                    sx={{ flexShrink: 0 }}
                                >
                                    Download CSV
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                maxHeight: {
                                    xs: "calc(100vh - 280px)",
                                    sm: "calc(100vh - 330px)",
                                },
                                overflow: "auto",
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                aria-label="File usage by user"
                                sx={{
                                    minWidth: isVideo ? 930 : 620,
                                    "& .MuiTableCell-root": {
                                        px: { xs: 1.5, sm: 2 },
                                        py: 1.25,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                minWidth: 190,
                                                fontWeight: 800,
                                                color: "text.primary",
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            User
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 800,
                                                color: "text.primary",
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            Views
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 800,
                                                color: "text.primary",
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            Comments
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 800,
                                                color: "text.primary",
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            Downloads
                                        </TableCell>
                                        {isVideo && (
                                            <>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: "text.primary",
                                                        backgroundColor:
                                                            "background.subtle",
                                                    }}
                                                >
                                                    Timelines
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: "text.primary",
                                                        backgroundColor:
                                                            "background.subtle",
                                                    }}
                                                >
                                                    Playlists
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: "text.primary",
                                                        backgroundColor:
                                                            "background.subtle",
                                                    }}
                                                >
                                                    Playlist shares
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={isVideo ? 7 : 4}
                                                align="center"
                                                sx={{ py: 5 }}
                                            >
                                                <Typography color="text.secondary">
                                                    No users match your search.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredRows.map((row, index) => {
                                        const isTotal = row.isTotal;

                                        return (
                                            <TableRow
                                                key={`${row.name}-${index}`}
                                                sx={{
                                                    backgroundColor: isTotal
                                                        ? "rgba(0, 149, 61, 0.06)"
                                                        : "background.paper",
                                                    "&:last-child td, &:last-child th":
                                                        {
                                                            borderBottom: 0,
                                                        },
                                                    "&:hover": {
                                                        backgroundColor:
                                                            isTotal
                                                                ? "rgba(0, 149, 61, 0.09)"
                                                                : "background.subtle",
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{
                                                        fontWeight: isTotal
                                                            ? 800
                                                            : 600,
                                                    }}
                                                >
                                                    {row.name}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: isTotal
                                                            ? 800
                                                            : 400,
                                                    }}
                                                >
                                                    {row.viewCount}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: isTotal
                                                            ? 800
                                                            : 400,
                                                    }}
                                                >
                                                    {row.commentCount}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: isTotal
                                                            ? 800
                                                            : 400,
                                                    }}
                                                >
                                                    {row.downloadCount}
                                                </TableCell>
                                                {isVideo && (
                                                    <>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                fontWeight:
                                                                    isTotal
                                                                        ? 800
                                                                        : 400,
                                                            }}
                                                        >
                                                            {
                                                                row.timelineCount
                                                            }
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                fontWeight:
                                                                    isTotal
                                                                        ? 800
                                                                        : 400,
                                                            }}
                                                        >
                                                            {
                                                                row.playlistCount
                                                            }
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                fontWeight:
                                                                    isTotal
                                                                        ? 800
                                                                        : 400,
                                                            }}
                                                        >
                                                            {
                                                                row.playlistShareCount
                                                            }
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    disableElevation
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
